import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { openContactForm } from "@/lib/contactForm";
import { guideHeadings, slugifyHeading, splitRelated } from "@/lib/guideHub";
import "./searchPages.css";

function Blocks({ blocks }) {
  return (
    <div className="ds-search-body">
      {(blocks || []).map((block, index) => {
        if (block.type === "h2") {
          const id = slugifyHeading(block.text);
          return <h2 id={id} key={index}>{block.text}</h2>;
        }
        if (block.type === "h3") {
          const id = slugifyHeading(block.text);
          return <h3 id={id} key={index}>{block.text}</h3>;
        }
        if (block.type === "ul") {
          return (
            <ul key={index}>
              {(block.items || []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          );
        }
        if (block.type === "ol") {
          return (
            <ol key={index}>
              {(block.items || []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          );
        }
        return <p key={index}>{block.text}</p>;
      })}
    </div>
  );
}

export function SearchPageShell({ children, testId, wide = false, flush = false }) {
  return (
    <div className="ds-search-page min-h-screen" data-testid={testId}>
      <Header />
      <main className={`ds-search-wrap${wide ? " ds-search-wrap--wide" : ""}${flush ? " ds-search-wrap--flush" : ""}`}>{children}</main>
      <Footer />
    </div>
  );
}

export function AnswerBlock({ answer }) {
  if (!answer) return null;
  return (
    <section className="ds-search-answer" data-testid="search-answer">
      <h2>핵심 답변</h2>
      <p>{answer}</p>
    </section>
  );
}

function RelatedGroup({ title, links }) {
  if (!links?.length) return null;
  return (
    <section className="ds-guide-related-group">
      <h2>{title}</h2>
      <ul>
        {links.map((link) => (
          <li key={link.href}>
            <Link to={link.href}>{link.label}</Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function SearchDocView({ item, related, kicker, preview = false, hubPath = "/guides", hubLabel = "가이드", hideBreadcrumb = false, contextualBack = null }) {
  if (!item) return <p>문서를 찾을 수 없습니다.</p>;
  const cta = item.cta_href || "/#contact";
  const headings = guideHeadings(item.body_blocks);
  const groups = splitRelated(related);
  const handleCta = (e) => {
    if (preview) {
      e.preventDefault();
      return;
    }
    if (cta === "/#contact" || cta === "#contact") {
      e.preventDefault();
      openContactForm();
    }
  };
  const isService = (item.path || "").startsWith("/services/");
  return (
    <article data-testid="search-doc" className="ds-guide-article">
      {contextualBack}
      {hideBreadcrumb ? null : (
      <nav className="ds-guide-breadcrumb" aria-label="breadcrumb">
        <Link to="/">홈</Link>
        <span aria-hidden="true">/</span>
        <Link to={isService ? "/services" : hubPath}>{isService ? "서비스" : hubLabel}</Link>
        <span aria-hidden="true">/</span>
        <span>{item.title}</span>
      </nav>
      )}
      <p className="ds-search-kicker">{item.category_label || kicker}</p>
      {hideBreadcrumb && item.layer_labels?.length ? (
        <p className="ds-search-meta">
          전문 영역{" "}
          {item.layer_labels.map((row) => (
            <Link key={row.id} to={row.href || "/capabilities"}>{row.label}</Link>
          ))}
        </p>
      ) : null}
      {hideBreadcrumb && item.industry_labels?.length ? (
        <p className="ds-search-meta">
          산업 적용{" "}
          {item.industry_labels.map((row) => (
            <Link key={row.id} to={row.href || "/capabilities"}>{row.label}</Link>
          ))}
        </p>
      ) : null}
      <h1 className="ds-search-h1">{item.title}</h1>
      {item.question ? <p className="ds-guide-question">{item.question}</p> : null}
      {item.summary ? <p className="ds-search-summary">{item.summary}</p> : null}
      <AnswerBlock answer={item.answer} />
      {headings.length > 1 ? (
        <nav className="ds-guide-toc" aria-label="목차">
          <p>이 글에서 다루는 내용</p>
          <ol>
            {headings.map((row) => (
              <li key={row.id}>
                <a href={`#${row.id}`}>{row.text}</a>
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
      <Blocks blocks={item.body_blocks} />
      {item.scope?.length ? (
        <section>
          <h2>작업 범위</h2>
          <ul>{item.scope.map((row) => <li key={row}>{row}</li>)}</ul>
        </section>
      ) : null}
      {item.features?.length ? (
        <section>
          <h2>기능</h2>
          <ul>{item.features.map((row) => <li key={row}>{row}</li>)}</ul>
        </section>
      ) : null}
      {item.related_questions?.length ? (
        <section>
          <h2>같이 묻는 질문</h2>
          <ul>{item.related_questions.map((row) => <li key={row}>{row}</li>)}</ul>
        </section>
      ) : null}
      <RelatedGroup title="관련 서비스" links={groups.service} />
      <RelatedGroup title="관련 포트폴리오 · 근거" links={groups.portfolio} />
      <RelatedGroup title="관련 가이드" links={groups.guide} />
      <RelatedGroup title="관련 페이지" links={groups.other} />
      {item.faqs?.length ? (
        <section data-testid="search-doc-faq" className="ds-guide-faq">
          <h2>자주 묻는 질문</h2>
          {item.faqs.map((faq) => (
            <article key={faq.question}>
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </article>
          ))}
        </section>
      ) : null}
      <div className="ds-search-meta">
        <span>작성 {item.author_name || "DSITESOLUTION"}</span>
        {isService && item.published_at ? <time dateTime={item.published_at}>발행 {String(item.published_at).slice(0, 10)}</time> : null}
        {isService && item.updated_at ? <time dateTime={item.updated_at}>수정 {String(item.updated_at).slice(0, 10)}</time> : null}
      </div>
      <section className="ds-guide-cta-band">
        <h2>{item.cta_label || "프로젝트 상담"}</h2>
        <p>범위가 정해지지 않아도 됩니다. 공개 사이트와 시스템 구축을 나눠 안내합니다.</p>
        <Link className="ds-search-cta" to={cta === "/#contact" ? "/" : cta} onClick={handleCta}>
          {item.cta_label || "상담 신청"}
        </Link>
      </section>
    </article>
  );
}

export default function SearchDocLayout(props) {
  return (
    <SearchPageShell testId={props.testId}>
      <SearchDocView {...props} />
    </SearchPageShell>
  );
}
